import { Injectable, inject } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { SaleVoucher, ValeParaUso, VoucherListParams } from '../models/voucher.model';
import { PaginatedResponse } from '../models/pagination.model';
import { normalizarListadoVales } from '../utils/voucher.util';
import { normalizarValeParaUso } from '../utils/voucher-checkout.util';

/**
 * Vales de saldo a favor (docs/SPEC_VALE_SALDO_A_FAVOR.md). Los vales se crean en la base al
 * procesar una devolución en vale; acá se listan y se imprime su ticket.
 */
@Injectable({
  providedIn: 'root'
})
export class VoucherService {
  private readonly supabase = inject(SupabaseService).client;

  /** Listado de vales: la base filtra por organización, sucursal, estado, código/cliente y fechas. */
  async getVouchers(params: VoucherListParams): Promise<PaginatedResponse<SaleVoucher>> {
    const { data, error } = await (this.supabase as any).rpc('get_sale_vouchers', {
      p_branch_id: params.branchId,
      p_status: params.status || null,
      p_search: params.search?.trim() || null,
      p_from: params.from ? params.from.toISOString() : null,
      p_to: params.to ? params.to.toISOString() : null,
      p_origin_sale_id: params.originSaleId || null,
      p_used_branch_id: params.usedBranchId || null,
      p_limit: params.pageSize,
      p_offset: params.pageIndex * params.pageSize
    });
    if (error) throw error;
    const { rows, total } = normalizarListadoVales(data);
    return { data: rows, total };
  }

  /** Vales emitidos por una venta (devoluciones en vale y anulación con NC en vale). */
  async getVouchersBySale(saleId: string): Promise<SaleVoucher[]> {
    const { data } = await this.getVouchers({ branchId: null, originSaleId: saleId, pageIndex: 0, pageSize: 50 });
    return data;
  }

  /** Vale emitido por una operación de devolución (null si la devolución no fue en vale). */
  async getVoucherIdByOperation(operationId: string): Promise<string | null> {
    const { data, error } = await (this.supabase as any)
      .from('sale_vouchers')
      .select('id')
      .eq('return_operation_id', operationId)
      .maybeSingle();
    if (error) throw error;
    return data?.id ?? null;
  }

  /**
   * Valida un código para usarlo como pago en la sucursal: importe y vencimiento, o el motivo por
   * el que no se puede (no existe, otra sucursal, usado, vencido). La venta lo vuelve a validar.
   */
  async getVoucherForUse(code: string, branchId: string): Promise<ValeParaUso> {
    const { data, error } = await (this.supabase as any).rpc('get_voucher_for_use', {
      p_code: code,
      p_branch_id: branchId
    });
    if (error) throw error;
    return normalizarValeParaUso(data);
  }

  /**
   * Si hay vales activos sin usar que se puedan usar en la sucursal: el checkout ofrece cargarlos
   * aunque el vale esté apagado en la organización (los emitidos se siguen pudiendo usar, si no
   * vencieron). Con sucursal cuentan los de esa sucursal y los de ámbito toda la organización; sin
   * sucursal, todos los de la organización (la RLS limita a su negocio).
   */
  async hasActiveVouchers(branchId: string | null): Promise<boolean> {
    let query = (this.supabase as any)
      .from('sale_vouchers')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'active')
      // El estado no pasa solo a vencido: un vale activo con la fecha pasada ya no se puede usar.
      .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`);
    if (branchId) query = query.or(`branch_id.eq.${branchId},valid_scope.eq.organization`);
    const { count, error } = await query;
    if (error) throw error;
    return (count ?? 0) > 0;
  }

  /** Vale nuevo emitido por el sobrante de los vales usados en una venta (null si no hubo). */
  async getRemainderVoucherId(saleId: string): Promise<string | null> {
    const { data, error } = await (this.supabase as any)
      .from('sale_vouchers')
      .select('id')
      .eq('origin_sale_id', saleId)
      .eq('origin_type', 'remainder')
      .maybeSingle();
    if (error) throw error;
    return data?.id ?? null;
  }

  /**
   * Genera el ticket del vale. La Edge Function lo guarda en Storage y devuelve un enlace firmado
   * de 2 minutos que termina en vale_sucursal_fecha_hora.pdf (así el visor del navegador lo guarda
   * con ese nombre). Cada impresión queda contada: las siguientes dicen "Reimpresión".
   */
  async getVoucherReceiptUrl(voucherId: string): Promise<string> {
    const { data: { session } } = await this.supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) {
      throw new Error('No se encontró una sesión activa para autenticar la solicitud.');
    }

    const { data, error } = await this.supabase.functions.invoke('generate-voucher-receipt', {
      body: { voucherId },
      headers: { Authorization: `Bearer ${token}` }
    });

    if (error || !data?.url) {
      throw new Error(error?.message || 'Error al generar el ticket del vale.');
    }
    return data.url as string;
  }

  /**
   * Abre el ticket del vale para imprimir. Si se pasa una pestaña ya abierta (abierta en el mismo
   * clic, para que el navegador no la bloquee), se usa esa.
   */
  async printVoucher(voucherId: string, tab: Window | null = null): Promise<void> {
    const url = await this.getVoucherReceiptUrl(voucherId);
    if (tab && !tab.closed) {
      tab.location.href = url;
    } else {
      window.open(url, '_blank');
    }
  }
}
